"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback } from "react";
import { fallbackLng } from "@/i18n/settings";
import useBoundStore from "@/store/bound-store";
import { buildLocalizedPath, getPathLanguage } from "@/utils/language";

interface LocalizedLinkProps extends React.ComponentProps<typeof Link> {
  href: string;
  children: React.ReactNode;
  showLocale?: boolean;
}

export function LocalizedLink({
  href,
  children,
  showLocale = false,
  onClick,
  ...props
}: LocalizedLinkProps) {
  const pathname = usePathname() ?? "";
  const currentLocale = getPathLanguage(pathname) ?? fallbackLng;
  const canClose = useBoundStore((state) => state.canClose);
  const setPendingNavigation = useBoundStore((state) => state.setPendingNavigation);
  const setShowCloseDialog = useBoundStore((state) => state.setShowCloseDialog);

  const localizedHref = buildLocalizedPath(href, currentLocale, showLocale);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!canClose) {
        event.preventDefault();
        setPendingNavigation(localizedHref);
        setShowCloseDialog(true);
        return;
      }

      if (onClick) {
        onClick(event);
      }
    },
    [canClose, localizedHref, setPendingNavigation, setShowCloseDialog, onClick]
  );

  return (
    <Link href={localizedHref} prefetch={true} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
}
